import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';


import { Auth, authState, createUserWithEmailAndPassword, idToken, signInWithEmailAndPassword, signInWithCustomToken, signOut } from '@angular/fire/auth';
import { Firestore, doc, docData, setDoc } from '@angular/fire/firestore';
import { browserSessionPersistence, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail, updatePassword } from 'firebase/auth';
import { firstValueFrom, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { User } from '@app/models/user';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import type { DocumentReference } from 'firebase/firestore';



@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: Observable<User | null>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: Auth,
    private firestore: Firestore,
    private route: ActivatedRoute
  ) {
    this.user = authState(this.auth).pipe(
      switchMap(firebaseUser => {
        if (firebaseUser) {
          const userDoc = doc(this.firestore, `users/${firebaseUser.uid}`) as DocumentReference<User>;
          return docData<User>(userDoc);
        } else {
          return of(null);
        }
      })
    );
  }

  signUp(userData: User, password: string) {
    return createUserWithEmailAndPassword(this.auth, userData.email, password)
      .then(credential => {
        if (!credential.user) {
          throw new Error('User credential missing after sign-up.');
        }
        userData.uid = credential.user.uid;
        this.updateUserData(userData);
        console.log('signUp %s', credential.user.email);
        return true;
      })
      .catch(err => console.log(err));
  }

  login(email: string, password: string): Promise<boolean> {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then(userCredential => {
        console.log('login %s', userCredential.user?.email); // || this.updateUserData(user.user);
        return true;
      })
      .catch(err => {
        console.log('login failed.');
        console.log(err);
        return false;
      });
  }



  // googleLogin() {
  //   const provider = new firebase.auth.GoogleAuthProvider();
  //   return this.oAuthLogin(provider);
  // }

  async logout() {
    await signOut(this.auth);
    await this.getUser(); // ここで更新しておかないと userがnullにならない
    this.router.navigate(['/home/login'], {relativeTo: this.route });
  }

  async logoutWithoutNavigate() {
    await signOut(this.auth);
    await this.getUser(); // ここで更新しておかないと userがnullにならない
  }

  // private oAuthLogin(provider) {
  //   return this.afAuth.auth.signInWithPopup(provider)
  //     .then(credential => {
  //       console.log(credential.user);
  //       return this.updateUserData(credential.user);
  //     })
  //     .catch(err => console.log(err));
  // }


  getUser() {
    return new Promise<User>((resolve, reject) => {
      this.user.subscribe(u => resolve(u));
    });
  }

  getAuthState() {
    return authState(this.auth);
  }

  private updateUserData(user: User) {
    console.log(user);
    const docUser = doc(this.firestore, `users/${user.uid}`);
    return setDoc(docUser, user);
  }

  getToken() {
    return firstValueFrom(idToken(this.auth)).then(token => token ?? '');
  }

  async resetPassword(email) {
    // const email = (await this.getUser()).email
    await sendPasswordResetEmail(this.auth, email);
  }

  async changePassword(currentPassword, newPassword) {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No authenticated user to update password.');
    }

    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );

    console.log(credential);

    // 再認証完了後にパスワードを変更する
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  authTerminal(serialId: string) {
    return new Promise<string>(async (resolve, reject) => {
      // 認証トークンをヘッダーに入れる。
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };

      let customToken: string;

      try {
        // 送信内容を組み立てる。
        const body = { serialId: serialId };
        // POSTする。
        console.log('post');
        this.http.post(
          environment.setting.functionUri + '/authTerminal',
          body,
          httpOptions)
          .subscribe((response: HttpResponse<any>) => {
            console.log('post response');
            customToken = response['customToken'];
            resolve(customToken);
          }, _err => { resolve(null); });
      } catch (err) {
        console.error('post terminalAuth error', err);
        reject(false);
      }
    });
  }

  async terminalLogin(serialId: string) {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const customToken = await this.authTerminal(serialId);
        if (!customToken) {
          resolve(false);
          return;
        }

        try {
          await this.auth.setPersistence(browserSessionPersistence);
        } catch (err) {
          console.error(err);
          resolve(false);
          return;
        }

        try {
          const userCredential = await signInWithCustomToken(this.auth, customToken);
          console.log('login %s', userCredential.user?.email); // || this.updateUserData(user.user);
          resolve(true);
        } catch (err) {
          console.error('login failed.');
          console.error(err);
          resolve(false);
        }
      } catch (error) {
        console.error(error);
        resolve(false);
      }
    });
  }

}
